import { DownloadOutlined, UploadOutlined, VisibilityOutlined } from '@mui/icons-material';
import {
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useRef } from 'react';

import Result from 'components/v2/common/Result';
import TableSkeleton from 'components/v2/common/tableSkeleton';

import { getRetainerTypeLabelKey } from 'helpers/applicationRetainers';
import {
    canUploadLegacyRetainer,
    clientAgreementRowKey,
    isAssessmentRow,
} from 'hooks/useClientAgreementRows';
import useTranslate from 'hooks/useTranslate';
import type { ClientAgreementRow } from 'types';

interface ClientAgreementsTableProps {
    rows: ClientAgreementRow[];
    loading: boolean;
    busyKey: string | null;
    onView: (row: ClientAgreementRow, key: string) => void;
    onDownload: (row: ClientAgreementRow, key: string) => void;
    onUpload: (row: ClientAgreementRow, file: File, key: string) => void;
    onSelectCase: (applicationId: number) => void;
}

const formatDate = (value?: string | null) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const RowActions: React.FC<{
    row: ClientAgreementRow;
    rowKey: string;
    busy: boolean;
    onView: (row: ClientAgreementRow, key: string) => void;
    onDownload: (row: ClientAgreementRow, key: string) => void;
    onUpload: (row: ClientAgreementRow, file: File, key: string) => void;
}> = ({ row, rowKey, busy, onView, onDownload, onUpload }) => {
    const { translate } = useTranslate();
    const inputRef = useRef<HTMLInputElement>(null);
    const canUpload = canUploadLegacyRetainer(row);

    return (
        <Stack direction="row" spacing={0.25} sx={{ justifyContent: 'flex-end' }}>
            <Tooltip title={translate('view')}>
                <span>
                    <IconButton
                        size="small"
                        disabled={busy}
                        onClick={() => onView(row, rowKey)}
                        aria-label={translate('view')}
                    >
                        {busy ? (
                            <CircularProgress size={16} />
                        ) : (
                            <VisibilityOutlined fontSize="small" />
                        )}
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title={translate('download')}>
                <span>
                    <IconButton
                        size="small"
                        disabled={busy}
                        onClick={() => onDownload(row, rowKey)}
                        aria-label={translate('download')}
                    >
                        <DownloadOutlined fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
            {canUpload ? (
                <>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="application/pdf"
                        hidden
                        onChange={(event) => {
                            const file = event.target.files?.[0];
                            event.target.value = '';
                            if (file) onUpload(row, file, rowKey);
                        }}
                    />
                    <Tooltip title={translate('uploadAgreement')}>
                        <span>
                            <IconButton
                                size="small"
                                disabled={busy}
                                onClick={() => inputRef.current?.click()}
                                aria-label={translate('uploadAgreement')}
                            >
                                <UploadOutlined fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </>
            ) : null}
        </Stack>
    );
};

/** `GET /users/:id/agreements`: one row per retainer; `agreement_type` null = legacy PDF. */
export const ClientAgreementsTable: React.FC<ClientAgreementsTableProps> = ({
    rows,
    loading,
    busyKey,
    onView,
    onDownload,
    onUpload,
    onSelectCase,
}) => {
    const { translate } = useTranslate();

    if (loading) {
        return <TableSkeleton />;
    }

    if (rows.length === 0) {
        return (
            <Result
                title={translate('noAgreementsYet')}
                description={translate('noAgreementsYetDescription')}
                sx={{ py: 3 }}
            />
        );
    }

    return (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{translate('type')}</TableCell>
                        <TableCell>{translate('service')}</TableCell>
                        <TableCell align="right">{translate('amount')}</TableCell>
                        <TableCell>{translate('dateSigned')}</TableCell>
                        <TableCell align="right">{translate('actions')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, index) => {
                        const key = clientAgreementRowKey(row, index);
                        const assessment = isAssessmentRow(row);
                        const typeLabel = assessment
                            ? translate('eligibilityAssessment')
                            : translate(getRetainerTypeLabelKey(row.agreement_type));
                        const signedOn = formatDate(row.candidate_signed_date);
                        const applicationId = row.application_id;
                        const canOpenCase = applicationId != null && !assessment;

                        return (
                            <TableRow
                                key={key}
                                hover={canOpenCase}
                                onClick={
                                    canOpenCase ? () => onSelectCase(applicationId) : undefined
                                }
                                sx={{ cursor: canOpenCase ? 'pointer' : 'default' }}
                            >
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={typeLabel}
                                        variant={
                                            row.agreement_type || assessment ? 'filled' : 'outlined'
                                        }
                                        sx={{ fontWeight: 600 }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {row.program_name || '—'}
                                    </Typography>
                                    {row.application_id ? (
                                        <Typography variant="caption" color="text.secondary">
                                            {`${translate('case')} #${row.application_id}`}
                                        </Typography>
                                    ) : null}
                                </TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    {row.value ?? '—'}
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {signedOn ?? translate('retainerStatusUnsigned')}
                                </TableCell>
                                <TableCell
                                    align="right"
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    <RowActions
                                        row={row}
                                        rowKey={key}
                                        busy={busyKey === key}
                                        onView={onView}
                                        onDownload={onDownload}
                                        onUpload={onUpload}
                                    />
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};
